import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef } from 'react';
import { useBreakpointValue, Box, Container, Stack, Image, useTheme, css } from '@chakra-ui/react';
import { Text } from '@outdoorly/text';
import { Cluster } from '@outdoorly/cluster';
import { ButtonNextLink, IconButton } from '@outdoorly/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@outdoorly/icons';
import propTypes from 'prop-types';
import NextImage from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper';
import { getImageLoader, getBlurUrl } from '@outdoorly/util';

var _excluded = ["contentMaxWidth", "content", "id", "autoplay"];
SwiperCore.use([Navigation, Pagination, Autoplay]);
var SLIDE_HEIGHTS = {
  HERO_LARGE: ['sm', 'sm', 'md', 'xl'],
  HERO_MEDIUM: ['sm', 'sm', 'sm', 'md'],
  HERO_SMALL: ['sm', 'sm']
};
var Slide = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var contentMaxWidthCss = _ref.contentMaxWidthCss,
      color = _ref.color,
      backgroundColor = _ref.backgroundColor,
      actions = _ref.actions,
      breadcrumbText = _ref.breadcrumbText,
      bigBackgroundImage = _ref.backgroundImage,
      mobileImage = _ref.mobileImage,
      largeLogo = _ref.largeLogo,
      smallLogo = _ref.smallLogo,
      headlineText = _ref.headlineText,
      title = _ref.title,
      subheaderText = _ref.subheaderText,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'HERO_LARGE' : _ref$type;
  var headerSize = useBreakpointValue(['3xl', '5xl', '6xl']) || '3xl';
  var backgroundImage = useBreakpointValue({
    base: mobileImage,
    md: bigBackgroundImage
  }) || bigBackgroundImage;
  return /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    position: "relative",
    backgroundColor: backgroundColor
  }, backgroundImage && /*#__PURE__*/React.createElement(Box, {
    filter: "brightness(80%)",
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0
  }, /*#__PURE__*/React.createElement(NextImage, {
    loader: getImageLoader(backgroundImage),
    src: backgroundImage,
    layout: "fill",
    placeholder: "blur",
    objectFit: "cover",
    blurDataURL: getBlurUrl(backgroundImage),
    quality: 100
  })), /*#__PURE__*/React.createElement(Container, {
    as: Stack,
    spacing: 6,
    maxW: contentMaxWidthCss,
    minHeight: SLIDE_HEIGHTS[type] || 'sm',
    maxHeight: SLIDE_HEIGHTS[type] || 'sm',
    centerContent: true,
    align: "center",
    justifyContent: "center",
    position: "relative",
    paddingX: [8, 8, 16, 16],
    paddingY: [10, 12]
  }, breadcrumbText && /*#__PURE__*/React.createElement(Text, {
    color: color,
    size: "lg",
    align: "center"
  }, breadcrumbText), headlineText && /*#__PURE__*/React.createElement(Text, {
    as: "h3",
    color: color,
    size: "xl",
    align: "center"
  }, headlineText), smallLogo && /*#__PURE__*/React.createElement(Image, {
    src: smallLogo,
    maxW: "full",
    maxH: 10
  }), largeLogo && /*#__PURE__*/React.createElement(Image, {
    src: largeLogo,
    maxW: "full",
    maxH: 20
  }), title && /*#__PURE__*/React.createElement(Text, {
    as: "h1",
    color: color,
    size: headerSize,
    fontWeight: "700",
    align: "center"
  }, title), subheaderText && /*#__PURE__*/React.createElement(Text, {
    as: "h2",
    color: color,
    size: "lg",
    align: "center"
  }, subheaderText), (actions === null || actions === void 0 ? void 0 : actions.length) && /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Cluster, {
    spacing: 2,
    justify: "center"
  }, actions.map(function (_ref2, index) {
    var text = _ref2.text,
        href = _ref2.href;
    return /*#__PURE__*/React.createElement(ButtonNextLink, {
      key: index,
      variant: "solid",
      colorScheme: "gray",
      route: href,
      width: 48,
      size: "lg"
    }, text);
  })))));
});
Slide.propTypes = {
  actions: propTypes.arrayOf(propTypes.shape({
    text: propTypes.string,
    href: propTypes.string
  })),
  backgroundColor: propTypes.string,
  backgroundImage: propTypes.string,
  mobileImage: propTypes.string,
  breadcrumbText: propTypes.string,
  color: propTypes.string,
  contentMaxWidthCss: propTypes.string,
  headlineText: propTypes.string,
  largeLogo: propTypes.string,
  smallLogo: propTypes.string,
  subheaderText: propTypes.string,
  title: propTypes.string,
  type: propTypes.oneOf(['HERO_LARGE', 'HERO_MEDIUM', 'HERO_SMALL'])
};

if (process.env.NODE_ENV !== 'production') {
  Slide.displayName = 'Slide';
}

var Hero = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var _ref3$contentMaxWidth = _ref3.contentMaxWidth,
      contentMaxWidth = _ref3$contentMaxWidth === void 0 ? '5xl' : _ref3$contentMaxWidth,
      content = _ref3.content,
      _ref3$id = _ref3.id,
      id = _ref3$id === void 0 ? 'hero' : _ref3$id,
      autoplay = _ref3.autoplay,
      props = _objectWithoutProperties(_ref3, _excluded);

  var theme = useTheme();
  var contentMaxWidthCss = css({
    maxWidth: contentMaxWidth
  })(theme).maxWidth;

  if (content.length === 1) {
    return /*#__PURE__*/React.createElement(Slide, _extends({}, content[0], {
      contentMaxWidthCss: contentMaxWidthCss,
      color: "scheme.textPrimaryInverse"
    }));
  }

  var buttonOffset = "max(0px, calc(calc(100% - ".concat(contentMaxWidthCss, ") / 2))");
  return /*#__PURE__*/React.createElement(Box, _extends({
    position: "relative"
  }, props), /*#__PURE__*/React.createElement(IconButton, {
    size: "lg",
    isRound: true,
    paddingX: 3,
    icon: /*#__PURE__*/React.createElement(ChevronLeftIcon, {
      width: 6,
      height: 6
    }),
    variant: "solid",
    position: "absolute",
    left: buttonOffset,
    top: "50%",
    transform: "translate(100%, -50%)",
    zIndex: "10",
    id: "swiper-prev-button-".concat(id),
    "aria-label": "Previous",
    boxShadow: "md",
    sx: {
      display: ['none', 'none', 'flex']
    }
  }), /*#__PURE__*/React.createElement(IconButton, {
    size: "lg",
    isRound: true,
    paddingX: 3,
    icon: /*#__PURE__*/React.createElement(ChevronRightIcon, {
      width: 6,
      height: 6
    }),
    variant: "solid",
    position: "absolute",
    right: buttonOffset,
    top: "50%",
    transform: "translate(-100%, -50%)",
    zIndex: "10",
    id: "swiper-next-button-".concat(id),
    "aria-label": "Next",
    boxShadow: "md",
    sx: {
      display: ['none', 'none', 'flex']
    }
  }), /*#__PURE__*/React.createElement(Swiper, {
    ref: ref,
    spaceBetween: 0,
    slidesPerView: 1,
    loop: true,
    navigation: {
      nextEl: "#swiper-next-button-".concat(id),
      prevEl: "#swiper-prev-button-".concat(id)
    },
    pagination: {
      clickable: true,
      renderBullet: function renderBullet(index, className) {
        return "\n            <div style=\"width: 3rem; height: 0.25rem; border-radius: 0.125rem; --swiper-pagination-color: white\" class=\"".concat(className, "\"></div>\n          ");
      }
    },
    autoplay: autoplay
  }, content.map(function (slide, index) {
    return /*#__PURE__*/React.createElement(SwiperSlide, {
      key: index
    }, /*#__PURE__*/React.createElement(Slide, _extends({}, slide, {
      contentMaxWidthCss: contentMaxWidthCss,
      color: "scheme.textPrimaryInverse"
    })));
  })));
});

if (process.env.NODE_ENV !== 'production') {
  Hero.displayName = 'Hero';
}

Hero.propTypes = {
  /**
   * The hero section will stretch to 100% of screen width. Often you want
   * content to be contained within a centered column that has a thinner max
   * width than 100% (for very wide monitors). Use any valid `maxWidth` value
   * from the theme or CSS to set the max width on that inner centered column.
   */
  contentMaxWidth: propTypes.string,
  content: propTypes.arrayOf(propTypes.shape(Slide.propTypes)),
  type: propTypes.oneOf(['HERO_LARGE', 'HERO_MEDIUM', 'HERO_SMALL']),
  id: propTypes.string,
  autoplay: propTypes.oneOf([propTypes.bool, propTypes.object])
};

export { Hero };
